import {FC, useContext, useEffect, useMemo, useRef, useState} from "react";
import {corePaths, IAdminUsersCoursesResponseEntry, ILocalizationEntity, IPaginatedResponse} from "@buildwitt/shapeshift-types";
import {AdminFieldContainer, AdminInputText, ConfirmDeleteEntityDialog} from "@buildwitt/shapeshift-common/v2/admin";
import {AdminTable, AdminTableProps} from "@/components/common/table";
import {AdminEntityPageContainerProps} from "@buildwitt/shapeshift-common/v2/admin"
import {IPlaylistEntity} from "../../../../../types/playlist";
import {cohortGroupValidationDoc} from "../../../../../validationDocs/user/cohortGroupValidationDoc";
import {IGroupEntityPayload} from "@buildwitt/shapeshift-types/entities/Group";
import {GRID_CHECKBOX_SELECTION_COL_DEF, GridLinkOperator} from "@mui/x-data-grid-pro";
import {INotificationSeverity, NotificationContext, useApi} from "@buildwitt/shapeshift-identity";
import {useValidate} from "@buildwitt/use-validate";
import {makeStyles} from "tss-react/mui";
import {Autocomplete, Box, Breadcrumbs, Button, Checkbox, FormControlLabel, LinearProgress, Paper, TextField, Typography} from "@mui/material";
import {PlaylistPlay, SummarizeOutlined} from "@mui/icons-material";
import {DraggableList} from "@buildwitt/shapeshift-common/v2/quiz";
import {AdminPlaylistContentPreviewItem} from "./AdminPlaylistContentPreviewItem";
import {ICourseEntity, IEntity} from "@buildwitt/shapeshift-types/entities";
import {IGroupCurriculumFlatList} from "@buildwitt/shapeshift-types/IGroupCurriculum";
import {IBaseSearchDTO} from "@/components/common/table/AdminTable";
import {Link, useNavigate, unstable_useBlocker as useBlocker} from "react-router-dom";
import ConfirmationDialog from "@/components/common/dialogs/ConfirmationDialog";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import useGroupsCertifications from "@/hooks/UseGroupsCertifications";
import AutocompleteListBox, {AutocompleteListBoxProps} from "@/components/common/AutocompleteListBox";
import AddCertificationModal from "@/pages/ManagerView/Pages/Playlists/AdminPlaylistPage/AddCertificationModal";
import CustomAutocomplete from "@/components/common/CustomAutocomplete";

interface IProps extends Partial<AdminEntityPageContainerProps<IPlaylistEntity, "group">> {
	playlistId?: number
}

const template: IGroupEntityPayload = {
	title: "", slug: "", content: ""
}

const useStyles = makeStyles()((theme) => ({
	adminPlaylistPageContent: {
		display: "flex",
		gap: theme.spacing(2),
		flexDirection: "column"
	},
	headerContainer: {
		display: "flex",
		justifyContent: "space-between"
	},
	buttonContainer: {
		display: "flex",
		gap: theme.spacing(2)
	},
	title: {
		fontSize: "34px",
		fontWeight: 600
	},
	breadcrumbs: {
		"& svg": {
			marginRight: theme.spacing(1),
			top: "2px",
			position: "relative",
			fontSize: "18px"
		}
	},
	adminPlaylistPageContentContainer: {
		display: "flex",
		gap: theme.spacing(3)
	},
	container: {
		flexGrow: 2,
		padding: theme.spacing(3),
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(3)
	},
	contentPreview: {
		padding: theme.spacing(3),
		width: "300px"
	},
	certificationContainer: {
		display: "flex",
		gap: theme.spacing(2),
		alignItems: "center",
		justifyContent: "space-between"
	},
	certificationAutocomplete: {
		flex: 1,
		maxWidth: "450px",
		"& .MuiInputLabel-root": {
			color: "#F9F9F9B2"
		}
	}
}));


export const AdminPlaylistPageContent: FC<IProps> = ({playlistId, ...props}) => {
	const {t} = useSSTranslation();
	const {playlistBasedCerts} = useDevCycleSdk();
	const [locales] = useApi<ILocalizationEntity[]>("/api/settings/i18n");
	const [entityApiResponse, getEntity] = useApi<IPlaylistEntity>();
	const [entity, setEntity] = useState<IPlaylistEntity>();
	const [formData, setFormData] = useState<IGroupEntityPayload & {curriculum: IGroupCurriculumFlatList[]}>({...template, curriculum: []});
	const [initialData, setInitialData] = useState<typeof formData>({...template, curriculum: []});
	const [deleteApiResponse, deleteApi] = useApi<IGroupEntityPayload & {curriculum: IGroupCurriculumFlatList[]}, FormData>();
	const [apiResponse, saveApi] = useApi<IPlaylistEntity, FormData>();
	const [saveRequired, setSaveRequired] = useState(false);
	const validation = useValidate<IGroupEntityPayload>(cohortGroupValidationDoc, formData);
	const [_validationResult, runValidate, _hasErrors, clearValidation] = validation ?? [];
	const {notify} = useContext(NotificationContext);
	const {classes} = useStyles();
	const [courses, getCourses] = useApi<IPaginatedResponse<ICourseEntity[]>>();
	const [selectedCourses, setSelectedCourses] = useState<number[]>([]);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const navigate = useNavigate();
	const [openExitPageModal, setOpenExitPageModal] = useState<boolean>(false);
	const [nextLocationPath, setNextLocationPath] = useState<string>("");
	const forceExitPageRef = useRef<boolean>(false);
	const [awardCertification, setAwardCertification] = useState<boolean>(false);
	const [selectedCertificate, setSelectedCertificate] = useState<{id: string; label: string} | null>(null);
	const [savedCertification, setSavedCertification] = useState<{id: string; label: string} | null>(null);
	const {certifications, handleSearchChange, loadMoreCertifications, hasNextPage, apiResult: certificationsApiResult} = useGroupsCertifications();
	const coursesFirstLoading = useRef<boolean>(true);

	useEffect(() => {
		setFormData({...template, curriculum: []})
		setInitialData({...template, curriculum: []})
		setEntity(undefined)
		setAwardCertification(false)
		setSelectedCertificate(null)
		setSavedCertification(null)
	}, [playlistId])

	useEffect(() => {
		if (awardCertification) {
			handleSearchChange("");
		}
	}, [awardCertification, handleSearchChange]);

	const prevCall = useRef<{playlistId: number}>();
	useEffect(() => {
		if(playlistId){
			getEntity({url: `/api/strapi-plugin-shapeshift-lms/cohort/group/${playlistId}`, data: {locale: "en"}})
			prevCall.current = {playlistId}
		}
	}, [playlistId])

	useEffect(() => {
		if(entity){
			const payload = IGroupEntityToIGroupEntityPayload(entity)
			setSelectedCourses(payload.curriculum.map(e => e.id).slice(0, 25))
			setFormData(payload)
			setInitialData(payload)

			if(playlistBasedCerts && entity.certificationId && entity.certificationName){
				const savedCert = {
					id: entity.certificationId,
					label: entity.certificationName
				};
				setAwardCertification(true);
				setSelectedCertificate(savedCert);
				setSavedCertification(savedCert);
			} else {
				setAwardCertification(false);
				setSelectedCertificate(null);
				setSavedCertification(null);
			}
		}
	}, [entity])

	useBlocker(({nextLocation}) => {
		if (verifyChangesPendingSaving() && !forceExitPageRef.current){
			setOpenExitPageModal(true);
			setNextLocationPath(nextLocation.pathname);
			return true;
		}

		return false;
	})

	const verifyChangesPendingSaving = () => {
		const {title: initTitle , curriculum: initCurriculum, certificationId: initCertificationId} = initialData;
		const {title, curriculum} = formData;

		const initCourseIds = initCurriculum.map(item => item.id);
		const courseIds = curriculum.map(item => item.id);

		const sameCurriculumItems = initCurriculum.length === curriculum.length &&
			[...initCourseIds, ...courseIds].filter((id, index, array) => array.indexOf(id) === index)
				.every(id => initCourseIds.indexOf(id) !== -1 && courseIds.indexOf(id) !== -1 && (initCourseIds.indexOf(id) === courseIds.indexOf(id)))

		return initTitle !== title.trim() || !sameCurriculumItems || initCertificationId !== selectedCertificate?.id;
	}

	const handleSubmit = (publish?: boolean): void => {
		const payloadFormData = new FormData();
		clearValidation?.();

		const _formData = publish === undefined ? formData : {...formData, publishedAt: publish ? new Date().toISOString() : null}
		const payload = {
			..._formData,
			...(awardCertification && selectedCertificate ? {certificationId: selectedCertificate.id} : {certificationId: null})
		};
		const data = JSON.stringify(payload);
		payloadFormData.append("data", data)

		if(publish && saveRequired){
			notify("Please save changes before publishing", "", INotificationSeverity.warning, 5000)
			return;
		}
		const isValid = runValidate?.();
		if((publish || entity?.publishedAt) && !isValid){
			notify("Some items require attention", "", INotificationSeverity.error, 5000)
			return;
		}

		setSaveRequired(false);
		saveApi({
			url: "/api/strapi-plugin-shapeshift-lms/cohort/group" + (playlistId ? `/${playlistId}` : ""),
			data: payloadFormData,
			method: playlistId ? "PUT" : "POST",
			config: {
				headers: {
					"Content-Type": "multipart/form-data"
				}
			}
		})
	}

	useEffect(() => {
		if(apiResponse.statusCode === 200 && apiResponse.data){
			if(apiResponse.data.id !== entity?.id){
				forceExitPageRef.current = true;
				notify(t("playlist_save_success", "Information saved successfully"), "", INotificationSeverity.success, 5000);
				navigate("/admin/playlists/" + apiResponse.data.id);
				return;
			}
			setEntity(apiResponse.data);
			notify(t("playlist_save_success", "Information saved successfully"), "", INotificationSeverity.success, 5000);

			if(playlistBasedCerts){
				forceExitPageRef.current = true;
				navigate("/admin/playlists/" + apiResponse.data.id);
			}
		}
		else if(apiResponse.statusCode === 403) {
			notify("Please contact your administrator ", "Forbidden", INotificationSeverity.warning, 3000);
		}
		else if(apiResponse.statusCode === 400 && !entity?.curriculum) {
			notify("Please add at least one course before saving the playlist", "Error", INotificationSeverity.error, 3000);
		}
		else if(apiResponse.statusCode === 400) {
			notify(t("playlist_save_error", "Unable to save information. Please try again."), "", INotificationSeverity.error, 5000);
		}
	}, [apiResponse.statusCode])

	const handleDelete = (): void=> {
		if(!playlistId)
			return;

		deleteApi({
			url: `/api/strapi-plugin-shapeshift-lms/cohort/group/${playlistId}`,
			method: "DELETE"
		});
	}

	useEffect(() => {
		if(!deleteApiResponse.statusCode)
			return;

		if(deleteApiResponse.statusCode === 200){
			notify("Deleted playlist", "Success", INotificationSeverity.success, 5000)
			navigate("/admin/playlists")
		}
		else {
			notify("Please try again ", "Failed to delete playlist", INotificationSeverity.error, 5000)
		}
	}, [deleteApiResponse])

	useEffect(() => {
		if((selectedCourses)?.length > 0){
			getCourses({url: "/api/strapi-plugin-shapeshift-lms/cohort/course", data: {
				page: 1,
				pageSize: 100,
				extendedAccess: true,
				filters: encodeURIComponent(JSON.stringify({
					operator: GridLinkOperator.And,
					conditions: [{attribute: "id", operator: "eq", value: selectedCourses}]
				}))
			}});
		}
		if(selectedCourses?.length === 0)
			setFormData(prev => ({...prev, curriculum: []}));
	}, [selectedCourses]);

	useEffect(() => {
		if(courses.data?.data?.length > 0 && !courses.isLoading){
			const curriculumPayload: IGroupCurriculumFlatList[] = courses.data.data.map(e => ({level: 0, dragId: e.id+"", id: e.id, slug: e.slug, title: e.title, type: corePaths.course}));
			
			if (coursesFirstLoading.current && playlistId){
				const initialCurriculum: IGroupCurriculumFlatList[] = [];
				entity.curriculum.forEach(curriculum => {
					const item = curriculumPayload.find(x => x.id === curriculum.id);
					if (item){
						initialCurriculum.push(item);
					}
				});
				setFormData(prev => ({...prev, curriculum: initialCurriculum }));
				coursesFirstLoading.current = false;
				return;
			}

			setFormData(prev => {
				//order the curriculum by existing order, with the new courses at the end
				//courses in existing curriculum which are not in new curriculum will be removed
				const newCurriculum = curriculumPayload;
				const prevIds = prev.curriculum.map(e => e.id);
				const newIds = newCurriculum.map(e => e.id);
				const newCurriculumOrdered = newCurriculum.sort((a, b) => {
					const aIndex = prevIds.indexOf(a.id);
					const bIndex = prevIds.indexOf(b.id);
					if(aIndex === -1 && bIndex === -1)
						return 0;
					else if(aIndex === -1)
						return 1;
					else if(bIndex === -1)
						return -1;
					else
						return aIndex - bIndex;
				});

				return {...prev, curriculum: newCurriculumOrdered}
			});
		}
	}, [courses]);

	useEffect(() => {
		const {errorData} = entityApiResponse;
		if(entityApiResponse.isError && errorData?.error?.message === "Failed to find localization"){
			notify("Please fill out a new translation and press save to add a translation for this playlist", "New Language", INotificationSeverity.neutral, 5000)
			setFormData(prev => ({...prev, slug: undefined}))
		}

		if(entityApiResponse.statusCode === 404){
			notify("Failed to find playlist", "Not Found", INotificationSeverity.error, 5000)
		}

		if(!entityApiResponse.data)
			return;

		setEntity(entityApiResponse.data)
	}, [entityApiResponse])


	const IGroupEntityToIGroupEntityPayload = (e: IPlaylistEntity): typeof formData => {
		const {title, slug, content, id, createdAt, updatedAt, description, curriculum} = e
		const payload = {
			title, slug, content, id, createdAt, updatedAt, description, curriculum: curriculum.map((e) => {
				const title = courses?.data?.data?.find?.(x => x.id === e.id)?.title || "...";
				return {...e, level: 0, dragId: e.id+"", slug: e.id+"",id: e.id, type: corePaths.course, title}
			})
		}
		return payload;
	}

	const handleExit = () => {
		forceExitPageRef.current = true;
		navigate(nextLocationPath);
	}

	const handleKeppEditing = () => {
		setNextLocationPath("");
		setOpenExitPageModal(false);
	}

	const handleCancel = () => {
		forceExitPageRef.current = true;
		navigate("../");
	}

	const coursesTableHeadCells = (locales?: ILocalizationEntity[]): AdminTableProps<"course", IAdminUsersCoursesResponseEntry>["columns"] => ([
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			type: "string",
			hideable: false
		},
		{
			field: "title",
			type: "string",
			flex: 1,
			headerName: "Course",
			minWidth: 240
		},
		{
			field: "moduleCount",
			type: "string",
			filterable: false,
			flex: 1,
			headerName: "Lessons",
			maxWidth: 110
		},
		{
			field: "languages",
			type: "string",
			flex: 1,
			filterable: false,
			headerName: "Language",
			valueGetter: ({value}: {value: IAdminUsersCoursesResponseEntry["languages"]}) => value?.map(e => locales?.find(x => x.code === e)?.name)?.join(", ")
		},
		{
			field: "contentType",
			type: "string",
			flex: 1,
			headerName: "Content Type"
		}
	]);
	const draggableListValue = useMemo(() => {
		return formData.curriculum.map(e => ({id: e.dragId, value: e.title || "..."}));
	}, [formData.curriculum]);

	const availableCertifications = useMemo(() => {
		if (savedCertification && !certifications.find(cert => cert.id === savedCertification.id)) {
			return [savedCertification, ...certifications];
		}
		return certifications;
	}, [certifications, savedCertification]);

	const [openModal, setOpenModal] = useState(false);
	const [newCertName, setNewCertName] = useState("");

	const handleOpenModal = (name: string) => {
		setNewCertName(name);
		setOpenModal(true);
	};

	const handleCloseModal = () => {
		setOpenModal(false);
		setNewCertName("");
	};

	return (
		<Box className={classes.adminPlaylistPageContent}>
			<Box className={classes.headerContainer}>
				<Box>
					<Breadcrumbs className={classes.breadcrumbs} aria-label="breadcrumb">
						<Link to={"/admin/playlists"}><Typography variant="body1"><PlaylistPlay /> Playlists</Typography></Link>
						<Typography><SummarizeOutlined/>{playlistId ? entity?.title : "Create"}</Typography>
					</Breadcrumbs>
					<Box padding={1}/>
					<Typography className={classes.title} variant="h1">{playlistId ? entity?.title : "Create Playlist"}</Typography>
				</Box>
				<Box className={classes.buttonContainer}>
					<Button variant="outlined" onClick={handleCancel}>Cancel</Button>
					<Button variant="contained" onClick={() => {handleSubmit(true)}}>Save</Button>
				</Box>
			</Box>
			<Box className={classes.adminPlaylistPageContentContainer}>
				<Paper className={classes.container}>
					<AdminInputText required onChange={e => setFormData(prev => ({...prev, title: e.target.value}))} value={formData.title} label={"Title"}/>
					{playlistBasedCerts && (
						<Box className={classes.certificationContainer}>
							<FormControlLabel
								control={
									<Checkbox
										checked={awardCertification}
										onChange={(e) => {
											setAwardCertification(e.target.checked);
											if (!e.target.checked) {
												setSelectedCertificate(null);
											}
										}}
										data-test-id="playlist-certification-checkbox"
										inputProps={{
											"aria-label": t("playlist_award_certification_label", "Award a certification when learners complete this playlist")
										}}
									/>
								}
								label={t("playlist_award_certification_label", "Award a certification when learners complete this playlist")}
							/>
							{awardCertification && (
								<CustomAutocomplete
									label={t("playlist_select_certificate_label", "Select Certificate")}
									options={availableCertifications}
									value={selectedCertificate}
									onChange={(_event, newValue) => setSelectedCertificate(newValue)}
									onInputChange={(_event, newInputValue, reason) => {
										if (reason !== "reset") {
											handleSearchChange(newInputValue.trim());
										}
									}}
									loading={certificationsApiResult.isLoading}
									hasNextPage={hasNextPage}
									isFetching={certificationsApiResult.isLoading}
									loadMoreOptions={loadMoreCertifications}
									apiResult={certificationsApiResult}
									handleOpenModal={handleOpenModal}
									allowAddNew={true}
								/>
							)}
						</Box>
					)}
					<AdminFieldContainer required label="Courses">
						<AdminTable<"course", IAdminUsersCoursesResponseEntry, IBaseSearchDTO<IEntity> & {extendedAccess?: boolean}>
							url="/api/strapi-plugin-shapeshift-lms/cohort/course"
							columns={coursesTableHeadCells(locales.data)}
							searchDTOverride={() => ({extendedAccess: true})}
							selectionModel={selectedCourses}
							onSelectionModelChange={e => {
								if(e.length > 25){
									notify("You can only select up to 25 courses", "Warning", INotificationSeverity.warning, 5000)
									return;
								}
								setSelectedCourses((e as number[]).slice(0, 25))
							}}
							type="course"
							initialState={{
								columns: {
									columnVisibilityModel: {
										updatedAt: false
									}}}}
						/>
					</AdminFieldContainer>
				</Paper>
				<Paper className={classes.contentPreview}>
					<Typography variant="h4">Content Preview</Typography>
					{draggableListValue.length === 0 && !courses.isLoading && <Typography paddingTop={2} variant="body1">Select courses to build your playlist</Typography>}
					{courses?.isLoading ? <Box height={0}><LinearProgress /></Box> :
						<DraggableList
							key={draggableListValue.map(e => e.value).sort().join(",")}
							list={draggableListValue}
							onChange={(values) => setFormData(prev => {
								const newValues = values as string[];
								const newCurriculum = newValues.map(e => (((prev.curriculum ?? []) as IGroupCurriculumFlatList[])?.find(x => x.title === e)))?.filter(e => !!e);

								//only worry about onChange for sorting
								if(newCurriculum.length !== (prev.curriculum ?? []).length)
									return prev;

								if(newCurriculum.map(e => e.id).join(",") !== (prev.curriculum ?? []).map(e => e.id).join(","))
									return ({...prev, curriculum: newCurriculum})
								return prev;
							})}
							componentOverrides={{
								draggableListItem: e => <AdminPlaylistContentPreviewItem
									{...e}
									key={e.item.id}
									onDelete={() => setSelectedCourses(prev => prev.filter(x => x !== parseInt(e.item.id)))}
								/>
							}}/>}
				</Paper>

			</Box>
			<Button sx={{width: "100px"}}onClick={() => setDeleteDialogOpen(true)}>DELETE</Button>
			{<ConfirmDeleteEntityDialog
				componentOverrides={{content: <>
					This playlist will be permanently deleted. Users will no longer see these courses in their Training Feed, but all their progress will be saved.
				</>}}
				open={deleteDialogOpen}
				onConfirm={() => handleDelete()}
				onClose={() => setDeleteDialogOpen(false)}
			/>}
			{
				openExitPageModal &&
				<ConfirmationDialog
					title={t("common_unsaved_data_title", "Unsaved data will be lost")}
					content={t("common_unsaved_changes_message", "You'll lose any unsaved work if you exit now. Do you want to proceed?")}
					testIdPrefix="unsavedChanges"
					open={openExitPageModal}
					hideCloseIcon
					cancelButtonTitle={t("common_exit", "Exit")}
					confirmButtonTitle={t("common_keep_editing", "Keep Editing")}
					onCancel={handleExit}
					onConfirm={handleKeppEditing}
					preventBackDropClick
				/>
			}
			{openModal && <AddCertificationModal
				open={openModal}
				defaultName={newCertName}
				onClose={handleCloseModal}
				onSave={(newCert) => {
					setSelectedCertificate(newCert);
					handleCloseModal();
				}}
			/>}
		</Box>
	);
}